import 'vanilla-calendar-pro/styles/index.css';
import './vanilla-calendar-base.scss';

import { type HTMLAttributes, useEffect, useRef, useState } from 'react';
import { Calendar, type DateMode, type Options } from 'vanilla-calendar-pro';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '@shared/redux';
import { appSettingsSelectors } from '@shared/redux/ducks/app-settings';
import { AppTheme } from '@shared/services/theme';

interface CalendarProps extends HTMLAttributes<HTMLDivElement> {
  config?: Options;
  selectedDates?: string[];
  selectedTime?: string;
  selectionDatesMode?: DateMode;
  onSetDate?: (date: string[], time?: string) => void;
}

export const VanillaCalendarBase = ({
  config,
  selectedDates = [],
  selectedTime,
  selectionDatesMode = 'multiple-ranged',
  onSetDate,
  ...attributes
}: CalendarProps) => {
  const { i18n } = useTranslation();
  const locale = i18n.language === 'ru' ? 'ru-RU' : 'en-US';
  const theme = useAppSelector(appSettingsSelectors.selectTheme);
  const selectedTheme = theme === AppTheme.dark ? 'dark' : 'light';
  const ref = useRef(null);
  const [calendar, setCalendar] = useState<Calendar | null>(null);
  const isUserClickingRef = useRef(false);

  const hasTime = Boolean(config?.selectionTimeMode);
  const defaultTime = dayjs().format('HH:mm');

  const sanitizedSelectedDates = selectedDates.map((dateStr) =>
    dayjs(dateStr).format('YYYY-MM-DD'),
  );

  const baseConfig: Options = {
    ...config,
    locale,
    selectedTheme,
    enableDateToggle: true,
    selectedDates: sanitizedSelectedDates,
    selectedTime: hasTime ? selectedTime || defaultTime : undefined,
    selectionDatesMode,
    onChangeTime(self) {
      onSetDate?.(self.context.selectedDates, self.context.selectedTime);
    },
    onClickDate(self) {
      isUserClickingRef.current = true;

      if (hasTime) {
        const includeTime = self.context.selectedTime;

        onSetDate?.(self.context.selectedDates, includeTime);
      } else {
        onSetDate?.(self.context.selectedDates);
      }

      setTimeout(() => {
        isUserClickingRef.current = false;
      }, 100);
    },
  };

  useEffect(() => {
    if (!ref.current) return;
    const instance = new Calendar(ref.current, baseConfig);
    setCalendar(instance);
    return () => instance.destroy();
  }, [locale, selectedTheme]);

  useEffect(() => {
    if (!calendar) return;
    calendar.init();
  }, [calendar]);

  useEffect(() => {
    if (!calendar) return;
    if (isUserClickingRef.current) return;

    calendar.set({ selectedDates: sanitizedSelectedDates });
    calendar.update({ dates: true });
  }, [sanitizedSelectedDates.join(',')]);

  return <div {...attributes} ref={ref} />;
};
